import { useEffect, useState } from "react";
import { logConnectorFailure, logConnectorSuccess } from "../../../../../telemetry";
import { useDocuparserClient } from "../../../hooks/useDocuparserClient";
import type { DocumentDetail } from "../../../services/docuparserApi";
import { DETAIL_ERROR_MESSAGES } from "../../../constants";

/**
 * Client-data hook for the document detail page — see
 * useDocuparserDocuments.ts's identical note on the one-hook-per-concern
 * convention this mirrors from MyQueue.
 */
export function useDocuparserDocumentDetail(documentId: number) {
  const client = useDocuparserClient();
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isUnmounted = false;
    setIsLoading(true);
    setError("");

    client
      .getDocumentDetails(documentId)
      .then((response) => {
        if (isUnmounted) return;
        if (response.success && response.data) {
          setDoc(response.data);
          logConnectorSuccess("docuparser", "detail-load");
        } else {
          setError(response.error || DETAIL_ERROR_MESSAGES.FETCH_FAILED);
        }
      })
      .catch((err: unknown) => {
        if (isUnmounted) return;
        setError(DETAIL_ERROR_MESSAGES.FETCH_ERROR);
        logConnectorFailure("docuparser", err, "detail-load-failed");
      })
      .finally(() => {
        if (!isUnmounted) setIsLoading(false);
      });

    return () => {
      isUnmounted = true;
    };
  }, [client, documentId]);

  return { doc, isLoading, error };
}
